import type { FormSchema } from '@cli-types/core-engine/schema.js'
import type { DevModeSnippet } from '@cli-types/next-adapter/generators.js'
import { SAMPLE_UUID } from '@cli-constants/generators.js'
import { toPascalCase } from '@cli-utils/text.js'
import { getAllFormSchemaFields } from '@core-engine/schema/field-helpers/form-fields/get-all-form-schema-fields.js'
import { isMultiStepForm } from '@core-engine/schema/field-helpers/form-fields/is-multi-step-form.js'
import { buildMultiStepFormComponentSource } from '@next-adapter/generators/form-pipeline/form-component-multistep/build-multi-step-form-component-source.js'
import { buildSingleStepFormComponentSource } from '@next-adapter/generators/form-pipeline/form-component-single/build-single-step-form-component-source.js'
import { renderBuildFormDevModeSnippetsRenderer } from '@next-adapter/templates/renderers/generators/dev-mode-integration/build-form-dev-mode-snippets.js'
import { createFormInputObject } from './create-form-input-object.js'
import { createFormSubmissionObject } from './create-form-submission-object.js'
import { createPayloadExample } from './create-payload-example.js'
import { createResponseExample } from './create-response-example.js'
import { formatTsObject } from './format-ts-object.js'
import { resolveActionModulePath } from './resolve-action-module-path.js'
export function buildFormDevModeSnippets(
  schema: FormSchema,
  actionImportPath: string,
  adminImportAlias?: string
): DevModeSnippet[] {
  const pascal = toPascalCase(schema.name)
  const fields = getAllFormSchemaFields(schema)
  const submissionInput = createFormInputObject(fields)
  const actionModulePath = resolveActionModulePath(actionImportPath, adminImportAlias)
  const submissionData = createFormSubmissionObject(fields)
  const submitResponse = { success: true, submission: submissionData }
  const submissionsResponse = { submissions: [submissionData], total: 1 }
  const deleteResponse = { success: true }
  const formLabel = schema.label
  const formComponentCode =
    isMultiStepForm(schema) && schema.steps!.length > 1
      ? buildMultiStepFormComponentSource(schema, { actionImportPath })
      : buildSingleStepFormComponentSource(schema, { actionImportPath })
  const submitActionCode = renderBuildFormDevModeSnippetsRenderer({
    kind: 'renderBuildFormDevModeSnippetsClientModuleTemplate',
    props: {
      pascal: pascal,
      actionModulePath: actionModulePath,
      tsObject: formatTsObject(submissionInput),
      toLowerCase: schema.label.toLowerCase()
    }
  })

  return [
    {
      id: 'jsx',
      label: `${formLabel} Component`,
      kind: 'jsx',
      icon: 'file-code',
      language: 'tsx',
      code: formComponentCode
    },
    {
      id: 'submit',
      label: `Submit ${formLabel}`,
      kind: 'mutation',
      icon: 'send',
      language: 'tsx',
      payloadExample: createPayloadExample(submissionInput),
      responseExample: createResponseExample(submitResponse),
      code: submitActionCode
    },
    {
      id: 'submissions',
      label: `${formLabel} Submissions`,
      kind: 'query',
      icon: 'inbox',
      language: 'tsx',
      responseExample: createResponseExample(submissionsResponse),
      code: renderBuildFormDevModeSnippetsRenderer({
        kind: 'renderBuildFormDevModeSnippetsImportsTemplate',
        props: {
          pascal: pascal,
          actionModulePath: actionModulePath,
          toLowerCase: schema.label.toLowerCase()
        }
      })
    },
    {
      id: 'delete',
      label: `Delete ${formLabel} Submission`,
      kind: 'mutation',
      icon: 'trash',
      language: 'tsx',
      payloadExample: createPayloadExample(SAMPLE_UUID),
      responseExample: createResponseExample(deleteResponse),
      code: renderBuildFormDevModeSnippetsRenderer({
        kind: 'renderBuildFormDevModeSnippetsClientModuleDetailsTemplate',
        props: {
          pascal: pascal,
          actionModulePath: actionModulePath
        }
      })
    }
  ]
}
